/**
 * Moderation Page
 * Review flagged content, manage user suspensions, and configure bot moderation settings
 */

import React, { useState, useEffect } from 'react';
import { useAdmin } from '../AdminApp';
import { ModerationQueue } from './ModerationQueuePage';
import { toast } from 'sonner';
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  User,
  MessageSquare,
  Eye,
  Ban,
  ChevronLeft,
  ChevronRight,
  Filter,
  Bot,
  Settings,
  Zap,
  AlertCircle,
  ToggleLeft,
  ToggleRight,
  RefreshCw,
  FileText,
  Activity,
  Trash2,
  AlertOctagon
} from 'lucide-react';

const statusColors = {
  pending: 'bg-amber-100 text-amber-700',
  reviewed: 'bg-blue-100 text-blue-700',
  dismissed: 'bg-gray-100 text-gray-700',
  action_taken: 'bg-rose-100 text-rose-700'
};

const violationTypeLabels = {
  inappropriate_content: 'Inappropriate Content',
  harassment: 'Harassment',
  spam: 'Spam',
  cheating: 'Cheating',
  hate_speech: 'Hate Speech',
  sexual_content: 'Sexual Content',
  violence: 'Violence',
  dangerous_activities: 'Dangerous Activities',
  illegal_activities: 'Illegal Activities',
  other: 'Other'
};

const ModerationPage = () => {
  const { api, admin } = useAdmin();
  // Round 13: the reports queue (user reports + auto-filter) is the first tab.
  const [activeTab, setActiveTab] = useState('reports');
  
  // Content Moderation State
  const [violations, setViolations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedViolation, setSelectedViolation] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewAction, setReviewAction] = useState('');

  // Bot Configuration State — Round 40: exactly what the server returns
  // (GET /api/admin/moderation/config, the settings Onyx runs on).
  const [botConfig, setBotConfig] = useState({
    enabled: null,
    sensitivity: 'medium',
    auto_warn: true,
    auto_mute: true,
    auto_suspend: false,
    auto_ban: false,
    human_review_required: true,
    monitored_violations: [],
    mute_threshold: 2,
    suspend_threshold: 4,
    categories: [],
  });
  const [botConfigLoading, setBotConfigLoading] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);

  // Bot Bans State
  const [botBans, setBotBans] = useState([]);
  const [botBansLoading, setBotBansLoading] = useState(false);
  const [botStats, setBotStats] = useState(null);

  // Danger Zone: Wipe All Posts state
  const isSuperAdmin = admin?.role === 'super_admin';
  const [wipeStatus, setWipeStatus] = useState(null); // {completed, completed_at, ...}
  const [wipeStatusLoading, setWipeStatusLoading] = useState(false);
  const [showWipeModal, setShowWipeModal] = useState(false);
  const [wipeConfirmText, setWipeConfirmText] = useState('');
  const [wipeRunning, setWipeRunning] = useState(false);
  const [wipeResult, setWipeResult] = useState(null);

  useEffect(() => {
    if (activeTab === 'content') {
      fetchViolations();
    } else if (activeTab === 'bot-settings') {
      fetchBotConfig();
      fetchBotStats();
    } else if (activeTab === 'bot-bans') {
      fetchBotBans();
    } else if (activeTab === 'danger-zone') {
      fetchWipeStatus();
    }
  }, [page, statusFilter, activeTab]);

  const fetchViolations = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: page.toString(), page_size: '20' });
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await api.get(`/api/admin/violations?${params}`);
      setViolations(response.data.violations);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load violations');
    } finally {
      setLoading(false);
    }
  };

  const fetchBotConfig = async () => {
    try {
      setBotConfigLoading(true);
      const response = await api.get('/api/admin/moderation/config');
      setBotConfig((prev) => ({ ...prev, ...(response.data || {}) }));
    } catch (error) {
      console.error('Failed to load bot config:', error);
      // Keep default config if fetch fails
    } finally {
      setBotConfigLoading(false);
    }
  };

  const fetchBotStats = async () => {
    try {
      const response = await api.get('/api/admin/moderation/stats');
      setBotStats(response.data);
    } catch (error) {
      console.error('Failed to load bot stats:', error);
    }
  };

  const fetchBotBans = async () => {
    try {
      setBotBansLoading(true);
      const response = await api.get('/api/admin/moderation/bot-bans');
      setBotBans(response.data.bans || []);
    } catch (error) {
      toast.error('Failed to load bot bans');
    } finally {
      setBotBansLoading(false);
    }
  };

  const saveBotConfig = async () => {
    try {
      setSavingConfig(true);
      // Round 40: only the settings — the server maps them onto Onyx
      const body = {
        enabled: botConfig.enabled,
        sensitivity: botConfig.sensitivity,
        auto_warn: botConfig.auto_warn,
        auto_mute: botConfig.auto_mute,
        auto_suspend: botConfig.auto_suspend,
        auto_ban: botConfig.auto_ban,
        human_review_required: botConfig.human_review_required,
        monitored_violations: botConfig.monitored_violations,
        mute_threshold: botConfig.mute_threshold,
        suspend_threshold: botConfig.suspend_threshold,
      };
      const response = await api.post('/api/admin/moderation/config', body);
      setBotConfig((prev) => ({ ...prev, ...(response.data || {}) }));
      fetchBotStats();
      toast.success('Saved — Onyx is using these settings now');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save bot configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  const reviewBotBan = async (banId, action) => {
    try {
      await api.post(`/api/admin/moderation/bot-bans/${banId}/review`, {
        action: action, // 'uphold' or 'overturn'
        admin_notes: reviewNotes
      });
      toast.success(`Ban ${action === 'uphold' ? 'upheld' : 'overturned'}`);
      fetchBotBans();
    } catch (error) {
      toast.error('Failed to review ban');
    }
  };

  const fetchWipeStatus = async () => {
    try {
      setWipeStatusLoading(true);
      const response = await api.get('/api/admin/posts/wipe-status');
      setWipeStatus(response.data);
    } catch (error) {
      console.error('Failed to load wipe status', error);
    } finally {
      setWipeStatusLoading(false);
    }
  };

  const runWipeAllPosts = async () => {
    if (wipeConfirmText !== 'WIPE_ALL_POSTS') {
      toast.error('You must type WIPE_ALL_POSTS exactly');
      return;
    }
    try {
      setWipeRunning(true);
      const response = await api.post('/api/admin/posts/wipe-all', {
        confirm: 'WIPE_ALL_POSTS',
      });
      setWipeResult(response.data);
      toast.success('All posts wiped');
      setShowWipeModal(false);
      setWipeConfirmText('');
      // Refresh status so the marker card shows up.
      fetchWipeStatus();
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 409) {
        // Already wiped — update the status pane instead of throwing.
        toast.info('Wipe already completed previously');
        setWipeResult(null);
        fetchWipeStatus();
        setShowWipeModal(false);
      } else if (status === 400) {
        toast.error(typeof detail === 'string' ? detail : 'Confirmation invalid');
      } else {
        toast.error('Wipe failed');
      }
    } finally {
      setWipeRunning(false);
    }
  };

  const openReviewModal = (violation) => {
    setSelectedViolation(violation);
    setReviewNotes('');
    setReviewAction('');
    setShowReviewModal(true);
  };

  const submitReview = async (status) => {
    try {
      await api.post(`/api/admin/violations/${selectedViolation.id}/review`, {
        violation_id: selectedViolation.id,
        status: status,
        admin_notes: reviewNotes,
        action_taken: reviewAction
      });
      toast.success('Violation reviewed');
      setShowReviewModal(false);
      fetchViolations();
    } catch (error) {
      toast.error('Failed to submit review');
    }
  };

  const suspendUser = async (userId, type) => {
    try {
      await api.post(`/api/admin/users/${userId}/suspend`, {
        user_id: userId,
        suspension_type: type,
        reason: reviewNotes || 'Violation review action',
        duration_days: type === 'temporary' ? 7 : null
      });
      toast.success('User suspended');
      setShowReviewModal(false);
      fetchViolations();
    } catch (error) {
      toast.error('Failed to suspend user');
    }
  };

  const updateConfigField = (field, value) => {
    setBotConfig(prev => ({ ...prev, [field]: value }));
  };

  const toggleViolationType = (type) => {
    setBotConfig(prev => {
      const currentViolations = prev.monitored_violations || [];
      return {
        ...prev,
        monitored_violations: currentViolations.includes(type)
          ? currentViolations.filter(t => t !== type)
          : [...currentViolations, type]
      };
    });
  };

  // Render Bot Settings Tab
  const renderBotSettings = () => (
    <div className="space-y-6">
      {/* Bot Stats Cards */}
      {botStats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-50 rounded-lg">
                <Bot className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{botStats.total_detections || 0}</p>
                <p className="text-sm text-gray-500">Total Detections</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-50 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{botStats.warnings_issued || 0}</p>
                <p className="text-sm text-gray-500">Warnings Issued</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-rose-50 rounded-lg">
                <Ban className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{botStats.bans_issued || 0}</p>
                <p className="text-sm text-gray-500">Bans Issued</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Activity className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{botStats.accuracy_rate == null ? '—' : `${botStats.accuracy_rate}%`}</p>
                <p className="text-sm text-gray-500" title="Of the detections a person reviewed, how many they agreed with">Upheld on review</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {botConfigLoading ? (
        <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 text-center">
          <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto" />
          <p className="text-gray-500 mt-2">Loading bot configuration...</p>
        </div>
      ) : (
        <>
          {/* Main Toggle */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gradient-to-br from-emerald-500 to-rose-500 rounded-xl">
                  <Bot className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">AI Bot Moderation (Onyx)</h3>
                  <p className="text-sm text-gray-500">
                    24/7 checks of live chat, posts, comments, profiles and direct messages — website and app. Same switch as AI Workforce → Onyx.
                    {botConfig.enabled == null && <span className="block text-amber-600">Onyx status unknown — the workforce may still be starting.</span>}
                  </p>
                </div>
              </div>
              <button
                onClick={() => updateConfigField('enabled', !botConfig.enabled)}
                className={`p-1 rounded-full transition-colors ${botConfig.enabled ? 'bg-emerald-500' : 'bg-gray-300'}`}
              >
                {botConfig.enabled ? (
                  <ToggleRight className="w-10 h-6 text-white" />
                ) : (
                  <ToggleLeft className="w-10 h-6 text-white" />
                )}
              </button>
            </div>
          </div>

          {/* Sensitivity Settings */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" />
              Detection Sensitivity
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Higher sensitivity catches more violations but may result in more false positives.
            </p>
            <div className="grid grid-cols-3 gap-3">
              {['low', 'medium', 'high'].map((level) => (
                <button
                  key={level}
                  onClick={() => updateConfigField('sensitivity', level)}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    botConfig.sensitivity === level
                      ? level === 'low' 
                        ? 'border-emerald-500 bg-emerald-50'
                        : level === 'medium'
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-rose-500 bg-rose-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-center">
                    <div className={`text-2xl mb-1 ${
                      level === 'low' ? 'text-emerald-500' :
                      level === 'medium' ? 'text-amber-500' : 'text-rose-500'
                    }`}>
                      {level === 'low' ? '🟢' : level === 'medium' ? '🟡' : '🔴'}
                    </div>
                    <p className="font-semibold text-gray-900 capitalize">{level}</p>
                    <p className="text-xs text-gray-500">
                      {level === 'low' && 'Fewer detections'}
                      {level === 'medium' && 'Balanced'}
                      {level === 'high' && 'More detections'}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Monitored Violations */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-500" />
              Monitored Violation Types
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Select which types of violations the bot should monitor for.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {(botConfig.categories || []).map((c) => ({
                id: c.id,
                label: c.label,
                icon: ({ nudity: '🔞', illegal: '🚫', violence: '⚔️', hate_speech: '🗣️', harassment: '😤', spam: '📧',
                  cheating: '🃏', profanity: '🤬', scam: '🎣', impersonation: '🎭', real_money: '💵' })[c.id] || '⚠️',
                critical: !!c.zero_tolerance,
              })).map((type) => (
                <button
                  key={type.id}
                  onClick={() => toggleViolationType(type.id)}
                  className={`p-3 rounded-xl border-2 transition-all text-left ${
                    (botConfig.monitored_violations || []).includes(type.id)
                      ? type.critical 
                        ? 'border-rose-500 bg-rose-50'
                        : 'border-emerald-500 bg-emerald-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{type.icon}</span>
                    <div>
                      <p className="font-medium text-sm text-gray-900">{type.label}</p>
                      {type.critical && (
                        <span className="text-xs text-rose-600">Zero Tolerance</span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Automated Actions */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5 text-gray-500" />
              Automated Actions
            </h3>
            
            <div className="space-y-4">
              {/* Auto Warn */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                  <p className="font-medium text-gray-900">Auto-Issue Warnings</p>
                  <p className="text-sm text-gray-500">First-time and minor violations get a warning automatically</p>
                </div>
                <button
                  onClick={() => updateConfigField('auto_warn', !botConfig.auto_warn)}
                  className={`p-1 rounded-full transition-colors ${botConfig.auto_warn ? 'bg-emerald-500' : 'bg-gray-300'}`}
                >
                  {botConfig.auto_warn ? (
                    <ToggleRight className="w-8 h-5 text-white" />
                  ) : (
                    <ToggleLeft className="w-8 h-5 text-white" />
                  )}
                </button>
              </div>

              {/* Round 40: Auto Mute (Onyx) */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                  <p className="font-medium text-gray-900">Auto-Mute in Live Chat</p>
                  <p className="text-sm text-gray-500">Harassment-level violations and repeat offenders are muted in chat for 1 hour</p>
                </div>
                <button
                  onClick={() => updateConfigField('auto_mute', !botConfig.auto_mute)}
                  className={`p-1 rounded-full transition-colors ${botConfig.auto_mute ? 'bg-emerald-500' : 'bg-gray-300'}`}
                >
                  {botConfig.auto_mute ? (
                    <ToggleRight className="w-8 h-5 text-white" />
                  ) : (
                    <ToggleLeft className="w-8 h-5 text-white" />
                  )}
                </button>
              </div>

              {/* Auto Suspend */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                  <p className="font-medium text-gray-900">Auto-Suspend Repeat Offenders</p>
                  <p className="text-sm text-gray-500">Suspends for 3 days at the suspend threshold — only when Require Human Review is off</p>
                </div>
                <button
                  onClick={() => updateConfigField('auto_suspend', !botConfig.auto_suspend)}
                  className={`p-1 rounded-full transition-colors ${botConfig.auto_suspend ? 'bg-rose-500' : 'bg-gray-300'}`}
                >
                  {botConfig.auto_suspend ? (
                    <ToggleRight className="w-8 h-5 text-white" />
                  ) : (
                    <ToggleLeft className="w-8 h-5 text-white" />
                  )}
                </button>
              </div>

              {/* Round 40: Auto Ban (Onyx) */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                  <p className="font-medium text-gray-900">Auto-Ban (Permanent)</p>
                  <p className="text-sm text-gray-500">Permanently bans for the most serious content — only when Require Human Review is off</p>
                </div>
                <button
                  onClick={() => updateConfigField('auto_ban', !botConfig.auto_ban)}
                  className={`p-1 rounded-full transition-colors ${botConfig.auto_ban ? 'bg-rose-500' : 'bg-gray-300'}`}
                >
                  {botConfig.auto_ban ? (
                    <ToggleRight className="w-8 h-5 text-white" />
                  ) : (
                    <ToggleLeft className="w-8 h-5 text-white" />
                  )}
                </button>
              </div>

              {/* Human Review Required */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                  <p className="font-medium text-gray-900">Require Human Review</p>
                  <p className="text-sm text-gray-500">Suspensions and bans wait for a person (Reports Queue / Decisions); warnings and mutes still happen automatically</p>
                </div>
                <button
                  onClick={() => updateConfigField('human_review_required', !botConfig.human_review_required)}
                  className={`p-1 rounded-full transition-colors ${botConfig.human_review_required ? 'bg-blue-500' : 'bg-gray-300'}`}
                >
                  {botConfig.human_review_required ? (
                    <ToggleRight className="w-8 h-5 text-white" />
                  ) : (
                    <ToggleLeft className="w-8 h-5 text-white" />
                  )}
                </button>
              </div>
            </div>

            {/* Thresholds */}
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mute Threshold
                </label>
                <input
                  type="number"
                  min="1"
                  max="30"
                  value={botConfig.mute_threshold ?? ''}
                  onChange={(e) => updateConfigField('mute_threshold', parseInt(e.target.value, 10) || 1)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <p className="text-xs text-gray-500 mt-1">Repeat violations in 30 days before a chat mute (first ones are warnings)</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Suspend Threshold
                </label>
                <input
                  type="number"
                  min="2"
                  max="30"
                  value={botConfig.suspend_threshold ?? ''}
                  onChange={(e) => updateConfigField('suspend_threshold', parseInt(e.target.value, 10) || 2)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <p className="text-xs text-gray-500 mt-1">Repeat violations before a suspension; a permanent ban is recommended 2 after that</p>
              </div>
            </div>
          </div>

          {/* Zero Tolerance Warning */}
          <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-rose-600 mt-0.5" />
              <div>
                <p className="font-semibold text-rose-800">Zero Tolerance Policy</p>
                <p className="text-sm text-rose-700">
                  Sexual content and anything involving minors is always recommended for a permanent ban and goes to the
                  top of the Reports Queue, at every sensitivity. A person confirms it unless Auto-Ban is on and Human Review is off.
                  Report child sexual abuse material to NCMEC (report.cybertip.org) — Onyx does not file reports for you.
                </p>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              onClick={saveBotConfig}
              disabled={savingConfig}
              className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-xl font-medium hover:from-emerald-600 hover:to-emerald-700 transition-all disabled:opacity-50 flex items-center gap-2"
            >
              {savingConfig ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Save Configuration
                </>
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );

  // Render Bot Bans Tab
  const renderBotBans = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">User</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Violation</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Confidence</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Date</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {botBansLoading ? (
                <tr>
                  <td colSpan="6" className="py-8 text-center text-gray-500">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Loading bot bans...
                  </td>
                </tr>
              ) : botBans.length === 0 ? (
                <tr>
                  <td colSpan="6" className="py-8 text-center text-gray-500">
                    <div className="flex flex-col items-center gap-2">
                      <Bot className="w-8 h-8 text-gray-400" />
                      <p>No bot-initiated bans to review</p>
                    </div>
                  </td>
                </tr>
              ) : (
                botBans.map((ban) => (
                  <tr key={ban.id} className="hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                          <User className="w-4 h-4 text-gray-500" />
                        </div>
                        <span className="text-sm font-medium text-gray-900">{ban.user_name || 'Unknown'}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm text-gray-700">
                        {ban.violation_label || violationTypeLabels[ban.violation_type] || ban.violation_type}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${
                              ban.confidence >= 90 ? 'bg-rose-500' :
                              ban.confidence >= 70 ? 'bg-amber-500' : 'bg-emerald-500'
                            }`}
                            style={{ width: `${ban.confidence ?? 0}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-600">{ban.confidence == null ? '—' : `${ban.confidence}%`}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {ban.created_at ? new Date(ban.created_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        ban.review_status === 'upheld' ? 'bg-rose-100 text-rose-700' :
                        ban.review_status === 'overturned' ? 'bg-emerald-100 text-emerald-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {ban.review_status || 'Pending Review'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-2">
                        {(!ban.review_status || ban.review_status === 'pending') && (
                          <>
                            <button
                              onClick={() => reviewBotBan(ban.id, 'uphold')}
                              className="px-3 py-1.5 text-sm font-medium text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                            >
                              Uphold
                            </button>
                            <button
                              onClick={() => reviewBotBan(ban.id, 'overturn')}
                              className="px-3 py-1.5 text-sm font-medium text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                            >
                              Overturn
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Content Moderation</h1>
          <p className="text-gray-500 text-sm mt-1">Review flagged content, manage violations, and configure bot moderation</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl p-1 shadow-sm border border-gray-100 inline-flex gap-1">
        <button
          onClick={() => setActiveTab('reports')}
          data-testid="moderation-tab-reports"
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'reports'
              ? 'bg-rose-600 text-white'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <AlertTriangle className="w-4 h-4" />
          Reports Queue
        </button>
        <button
          onClick={() => setActiveTab('content')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'content' 
              ? 'bg-emerald-500 text-white' 
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Shield className="w-4 h-4" />
          Legacy Violations
        </button>
        <button
          onClick={() => setActiveTab('bot-settings')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'bot-settings' 
              ? 'bg-emerald-500 text-white' 
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Bot className="w-4 h-4" />
          Bot Settings
        </button>
        <button
          onClick={() => setActiveTab('bot-bans')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'bot-bans' 
              ? 'bg-emerald-500 text-white' 
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Ban className="w-4 h-4" />
          Bot Bans
        </button>
        {isSuperAdmin && (
          <button
            data-testid="moderation-tab-danger-zone"
            onClick={() => setActiveTab('danger-zone')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'danger-zone'
                ? 'bg-rose-600 text-white'
                : 'text-rose-600 hover:bg-rose-50'
            }`}
          >
            <AlertOctagon className="w-4 h-4" />
            Danger Zone
          </button>
        )}
      </div>

      {/* Tab Content */}
      {activeTab === 'reports' && <ModerationQueue embedded />}
      {activeTab === 'content' && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-50 rounded-lg">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {violations.filter(v => v.status === 'pending').length}
                  </p>
                  <p className="text-sm text-gray-500">Pending</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <Eye className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {violations.filter(v => v.status === 'reviewed').length}
                  </p>
                  <p className="text-sm text-gray-500">Reviewed</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-rose-50 rounded-lg">
                  <Ban className="w-5 h-5 text-rose-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {violations.filter(v => v.status === 'action_taken').length}
                  </p>
                  <p className="text-sm text-gray-500">Actions Taken</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-50 rounded-lg">
                  <XCircle className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {violations.filter(v => v.status === 'dismissed').length}
                  </p>
                  <p className="text-sm text-gray-500">Dismissed</p>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-4">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="reviewed">Reviewed</option>
                <option value="action_taken">Action Taken</option>
                <option value="dismissed">Dismissed</option>
              </select>
            </div>
          </div>

          {/* Violations List */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Type</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Reported User</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Reporter</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Date</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Status</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="py-8 text-center text-gray-500">
                        Loading violations...
                      </td>
                    </tr>
                  ) : violations.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="py-8 text-center text-gray-500">
                        <div className="flex flex-col items-center gap-2">
                          <CheckCircle className="w-8 h-8 text-emerald-500" />
                          <p>No violations to review</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    violations.map((violation) => (
                      <tr key={violation.id} className="hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-500" />
                            <span className="text-sm text-gray-900">
                              {violationTypeLabels[violation.violation_type] || violation.violation_type}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm text-gray-900">{violation.reported_user?.name || 'Unknown'}</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm text-gray-600">{violation.reporter?.name || 'System'}</span>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {violation.created_at ? new Date(violation.created_at).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[violation.status] || 'bg-gray-100 text-gray-700'}`}>
                            {violation.status?.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => openReviewModal(violation)}
                              className="px-3 py-1.5 text-sm font-medium text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                            >
                              Review
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
              <p className="text-sm text-gray-500">Total: {total} violations</p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-gray-600">Page {page}</span>
                <button
                  onClick={() => setPage(page + 1)}
                  className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === 'bot-settings' && renderBotSettings()}
      {activeTab === 'bot-bans' && renderBotBans()}

      {activeTab === 'danger-zone' && isSuperAdmin && (
        <div className="space-y-6" data-testid="danger-zone-panel">
          <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-rose-100 rounded-lg">
                <AlertOctagon className="w-6 h-6 text-rose-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-bold text-rose-900">
                  Wipe All Posts (one-time)
                </h2>
                <p className="text-sm text-rose-700 mt-1">
                  Deletes <strong>every</strong> post, comment, and post-like from the
                  social feed and resets per-user <code>posts_count</code> to 0.
                  A full backup is taken FIRST into{' '}
                  <code>posts_backup_&lt;timestamp&gt;</code> and{' '}
                  <code>comments_backup_&lt;timestamp&gt;</code>. This action can only
                  run <strong>once per database</strong> — a permanent marker prevents
                  accidental re-runs. Does <strong>not</strong> touch balances,
                  follows, streams, messages, or transactions.
                </p>
              </div>
            </div>
          </div>

          {wipeStatusLoading ? (
            <div className="bg-white rounded-xl p-6 text-center text-gray-500">
              Loading status…
            </div>
          ) : wipeStatus?.completed ? (
            <div className="bg-white rounded-xl p-6 border border-gray-200" data-testid="wipe-already-done-card">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                <h3 className="font-semibold text-gray-900">
                  Already wiped on{' '}
                  {new Date(wipeStatus.completed_at).toLocaleString()}
                </h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Posts deleted</p>
                  <p className="font-bold text-gray-900">{wipeStatus.posts_deleted}</p>
                </div>
                <div>
                  <p className="text-gray-500">Comments deleted</p>
                  <p className="font-bold text-gray-900">{wipeStatus.comments_deleted}</p>
                </div>
                <div>
                  <p className="text-gray-500">Likes deleted</p>
                  <p className="font-bold text-gray-900">{wipeStatus.post_likes_deleted}</p>
                </div>
                <div className="col-span-2 sm:col-span-3 pt-2 border-t border-gray-100">
                  <p className="text-gray-500 mb-1">Backups</p>
                  <p className="font-mono text-xs text-gray-800 break-all">
                    posts → {wipeStatus.posts_backup || '(source was empty)'}
                  </p>
                  <p className="font-mono text-xs text-gray-800 break-all">
                    comments → {wipeStatus.comments_backup || '(source was empty)'}
                  </p>
                </div>
                {wipeStatus.triggered_by && (
                  <div className="col-span-2 sm:col-span-3 pt-2 border-t border-gray-100">
                    <p className="text-gray-500">Triggered by</p>
                    <p className="font-mono text-xs text-gray-800">
                      {wipeStatus.triggered_by}
                      {wipeStatus.triggered_via ? ` (${wipeStatus.triggered_via})` : ''}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <button
                data-testid="open-wipe-modal-btn"
                onClick={() => {
                  setWipeConfirmText('');
                  setWipeResult(null);
                  setShowWipeModal(true);
                }}
                className="inline-flex items-center gap-2 px-5 py-3 bg-rose-600 hover:bg-rose-700 text-white rounded-lg font-semibold shadow-sm"
              >
                <Trash2 className="w-4 h-4" />
                Wipe All Posts (one-time)
              </button>
              <p className="text-xs text-gray-500 mt-3">
                You will be asked to type <code>WIPE_ALL_POSTS</code> to confirm.
              </p>
            </div>
          )}

          {wipeResult && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6" data-testid="wipe-result-card">
              <h3 className="font-semibold text-emerald-900 mb-3">Wipe complete</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-emerald-700">Posts deleted</p>
                  <p className="font-bold text-emerald-900" data-testid="wipe-result-posts">
                    {wipeResult.posts_deleted}
                  </p>
                </div>
                <div>
                  <p className="text-emerald-700">Comments deleted</p>
                  <p className="font-bold text-emerald-900" data-testid="wipe-result-comments">
                    {wipeResult.comments_deleted}
                  </p>
                </div>
                <div>
                  <p className="text-emerald-700">Likes deleted</p>
                  <p className="font-bold text-emerald-900" data-testid="wipe-result-likes">
                    {wipeResult.likes_deleted}
                  </p>
                </div>
                <div className="col-span-2 sm:col-span-3 pt-2 border-t border-emerald-100">
                  <p className="text-emerald-700 mb-1">Backups</p>
                  <p className="font-mono text-xs text-emerald-900 break-all" data-testid="wipe-result-backup-posts">
                    posts → {wipeResult.backup_posts || '(source was empty)'}
                  </p>
                  <p className="font-mono text-xs text-emerald-900 break-all" data-testid="wipe-result-backup-comments">
                    comments → {wipeResult.backup_comments || '(source was empty)'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Wipe Confirm Modal */}
      {showWipeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full" data-testid="wipe-confirm-modal">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-xl font-bold text-rose-700 flex items-center gap-2">
                <AlertOctagon className="w-5 h-5" />
                Confirm Wipe All Posts
              </h2>
              <button
                onClick={() => setShowWipeModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-700">
                This will delete every post, comment, and post-like on the platform.
                Backups will be created first. This action can only be executed
                <strong> once per database</strong>.
              </p>
              <p className="text-sm text-gray-700">
                To proceed, type <code className="px-1.5 py-0.5 bg-gray-100 rounded font-mono">WIPE_ALL_POSTS</code> below.
              </p>
              <input
                type="text"
                value={wipeConfirmText}
                onChange={(e) => setWipeConfirmText(e.target.value)}
                placeholder="WIPE_ALL_POSTS"
                data-testid="wipe-confirm-input"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 font-mono"
                disabled={wipeRunning}
              />
            </div>
            <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => setShowWipeModal(false)}
                disabled={wipeRunning}
                className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-700 text-sm font-medium disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                data-testid="wipe-confirm-btn"
                onClick={runWipeAllPosts}
                disabled={wipeRunning || wipeConfirmText !== 'WIPE_ALL_POSTS'}
                className="px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                {wipeRunning ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Wiping…
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Wipe All Posts
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && selectedViolation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Review Violation</h2>
                <button
                  onClick={() => setShowReviewModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-sm text-gray-500">Violation Type</p>
                <p className="font-medium text-gray-900">
                  {violationTypeLabels[selectedViolation.violation_type] || selectedViolation.violation_type}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Reported User</p>
                <p className="font-medium text-gray-900">{selectedViolation.reported_user?.name}</p>
              </div>
              {selectedViolation.description && (
                <div>
                  <p className="text-sm text-gray-500">Description</p>
                  <p className="text-gray-700">{selectedViolation.description}</p>
                </div>
              )}
              <div>
                <label className="text-sm text-gray-500 block mb-2">Admin Notes</label>
                <textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  rows="3"
                  placeholder="Add notes about this review..."
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-100 flex flex-wrap gap-3">
              <button
                onClick={() => submitReview('dismissed')}
                className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-700 text-sm font-medium"
              >
                Dismiss
              </button>
              <button
                onClick={() => submitReview('reviewed')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
              >
                Mark Reviewed
              </button>
              <button
                onClick={() => {
                  suspendUser(selectedViolation.reported_user_id, 'warning');
                  submitReview('action_taken');
                }}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium"
              >
                Warn User
              </button>
              <button
                onClick={() => {
                  suspendUser(selectedViolation.reported_user_id, 'temporary');
                  submitReview('action_taken');
                }}
                className="px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 text-sm font-medium"
              >
                Suspend User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModerationPage;
